import React, { useCallback, useContext, useState } from 'react';

import { Alert } from 'antd-v5';
import { isEmpty, isNil } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { youthAdvRequirementsParamsSel } from '@modules/youthProfile/duck/selectors';
import { Button, Spin } from '@shared/components';
import TextArea from '@shared/components/Input/TextArea';
import type { advancementTypes } from '@shared/constants';
import { offlineSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { userIdSel } from '@user';

import { maxAdvancementsComments } from '../../../constants';
import { AdvRequirementsContext } from '../context';
import AddPhoto from './AddPhoto';
import styles from './AdvRequirementsComments.module.scss';
import Comment from './Comment';
import CommentImage from './CommentImage';
import { useAdvancementComments } from './use-advancement-comments';

const loadingState = (
  <div className={styles.requirementCommentLoaderContainer}>
    <Spin size="large" />
  </div>
);

type RequirementCommentsProps = {
  disabled?: boolean;
  renderComments?: boolean;
  renderPhotos?: boolean;
  requirementId?: number;
  requirementName?: string;
  // Keeps the page's advancement status in sync with its comments, see useAdvancementComments
  syncStartedStatus?: boolean;
  youthUserId?: number;
  advancementId?: number;
  advancementType?: advancementTypes;
  versionId?: number | string | null;
};

const RequirementComments = (props: RequirementCommentsProps) => {
  const {
    disabled = false,
    renderComments = true,
    renderPhotos = true,
    requirementId,
    requirementName,
    syncStartedStatus = false,
  } = props;
  const pageParams = useSelector(youthAdvRequirementsParamsSel) ?? {};
  const { activeVersionId } = useContext(AdvRequirementsContext);
  const youthUserId = props.youthUserId ?? pageParams.userId;
  const advancementId = props.advancementId ?? pageParams.advancementId;
  const advancementType = props.advancementType ?? pageParams.advancementType;
  const versionId =
    props.versionId !== undefined ? props.versionId : activeVersionId;

  const {
    addComment,
    comments,
    deleteComment,
    deleteCommentPhoto,
    editComment,
    loading,
  } = useAdvancementComments(
    youthUserId,
    advancementId,
    advancementType,
    versionId,
    requirementId,
    { syncStartedStatus },
  );

  const [newCommentMessage, setNewCommentMessage] = useState('');
  const [photo, setPhoto] = useState<File>();

  const handlePostComment = useCallback(async () => {
    try {
      await addComment(newCommentMessage, photo);

      setNewCommentMessage('');
      setPhoto(undefined);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error: unknown) {
      // error catched in addComment
    }
  }, [addComment, newCommentMessage, photo, setPhoto]);
  const handleAddPhoto = useCallback(photo => setPhoto(photo), [setPhoto]);
  const handleDeletePhoto = useCallback(() => setPhoto(undefined), [setPhoto]);

  const isOffline = useSelector(offlineSel);
  const isDisabled = disabled || isEmpty(newCommentMessage) || isOffline;
  const viewerMemberId = useSelector(userIdSel);

  if (!renderComments) {
    return null;
  }

  if (loading) return loadingState;

  return (
    <div className={styles.requirementComments}>
      <>
        <h2>
          <FormattedMessage
            id="AdvRequirementsComments.comments"
            values={{
              count: comments.length,
              max: maxAdvancementsComments,
            }}
          />
        </h2>
        {requirementName && (
          <div>
            <h4>
              <FormattedMessage id="AdvRequirementsComments.requirement" />
            </h4>
            <div
              className={styles.requirementCommentRequirementName}
              dangerouslySetInnerHTML={{ __html: requirementName }}
            />
          </div>
        )}
      </>
      {!isEmpty(comments) && (
        <div className={styles.requirementCommentsList}>
          {comments.map(
            ({ author, authorImage, date, id, message, photos, userId }) => (
              <Comment
                author={author}
                authorImage={authorImage}
                commentId={id}
                date={date}
                key={id}
                message={message}
                onDeleteComment={deleteComment}
                onDeleteCommentPhoto={deleteCommentPhoto}
                onEditComment={editComment}
                photos={photos}
                renderPhotos={renderPhotos}
                userId={userId}
                viewerMemberId={viewerMemberId}
              />
            ),
          )}
        </div>
      )}
      <div className={styles.requirementCommentForm}>
        <div className={styles.requirementCommentInput}>
          <TextArea
            autoSize={{ minRows: 2 }}
            charCounter
            disabled={disabled}
            label={intl.formatMessage({
              id: 'AdvRequirementsComments.comment',
            })}
            maxLength={200}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setNewCommentMessage(event.target.value)
            }
            placeholder={intl.formatMessage({
              id: 'AdvRequirementsComments.addComment',
            })}
            value={newCommentMessage}
          />
        </div>
        <div className={styles.requirementCommentFormActions}>
          {renderPhotos && (
            <div className={styles.requirementCommentFormMediaActions}>
              {!isNil(photo) && (
                <CommentImage photo={photo} onDeletePhoto={handleDeletePhoto} />
              )}
              {isNil(photo) && (
                <AddPhoto setPictureUrl={handleAddPhoto} disabled={disabled} />
              )}
            </div>
          )}
          <Button
            disabled={isDisabled}
            onClick={handlePostComment}
            shape="round"
            size="large"
            type="primary"
          >
            <FormattedMessage id="AdvRequirementsComments.addComment" />
          </Button>
        </div>
      </div>
      {!!requirementId && (
        <Alert
          type="info"
          message={
            <FormattedMessage id="AdvRequirementsComments.RetentionNotice" />
          }
        />
      )}
    </div>
  );
};

export default RequirementComments;
